/**
 * Fias Leads — import target-field descriptors per entity type.
 *
 * The `field` names are the server's row-schema keys
 * (services/api-service/.../import-schemas.ts). Synonym regexes run
 * against normalizeHeader() output (lowercased, spaces/underscores/
 * hyphens/dots stripped), so /^first(name)?$/ matches "First Name",
 * "first_name", and "FIRST-NAME" alike.
 *
 * Within a list, more specific fields come first — autoMapColumns
 * consumes each header once in list order, so e.g. the deal list must
 * claim "Company Name" for company_name before "name" could swallow it
 * (protected here by ordering + the company|account prefix).
 */

import type { ImportEntityType, ImportFieldDescriptor } from './csv-mapping';

export const CONTACT_IMPORT_FIELDS: ReadonlyArray<ImportFieldDescriptor> = [
  {
    field: 'firstName',
    label: 'First name',
    required: true,
    synonyms: [/^first(name)?$/, /^fname$/, /^givenname$/, /^forename$/, /^namefirst$/],
  },
  {
    field: 'lastName',
    label: 'Last name',
    synonyms: [/^last(name)?$/, /^lname$/, /^surname$/, /^familyname$/, /^namelast$/],
  },
  {
    field: 'primaryEmail',
    label: 'Email',
    hint: 'A cell with several addresses keeps them all — first becomes primary',
    synonyms: [/^e?mail(address(es)?)?$/, /^primaryemail$/, /^(work|business)email$/],
  },
  {
    field: 'altEmails',
    label: 'Other emails',
    hint: 'Saved as alternate addresses on the contact',
    synonyms: [
      /^(secondary|other|alt(ernate)?)e?mails?(address(es)?)?$/,
      /^e?mail(address)?2$/,
      /^homeemail$/,
    ],
  },
  {
    field: 'primaryPhone',
    label: 'Phone',
    synonyms: [
      /^phone(number)?$/,
      /^primaryphone$/,
      /^(mobile|cell)(phone|number)?$/,
      /^telephone$/,
      /^tel$/,
      /^workphone$/,
    ],
  },
  {
    field: 'title',
    label: 'Job title',
    synonyms: [/^(job)?title$/, /^position$/, /^(job)?role$/],
  },
  {
    field: 'companyName',
    label: 'Company',
    hint: 'Links to an existing company by name',
    synonyms: [/^(company|account|organi[sz]ation)(name)?$/, /^employer$/],
  },
  {
    field: 'lifecycleStage',
    label: 'Lifecycle stage',
    synonyms: [/^lifecycle(stage)?$/, /^lead(status|stage)$/, /^contactstage$/],
  },
  {
    field: 'tags',
    label: 'Tags',
    hint: 'Comma-separated',
    synonyms: [/^tags?$/, /^labels?$/, /^groups?$/],
  },
  {
    field: 'notes',
    label: 'Notes',
    synonyms: [/^notes?$/, /^comments?$/],
  },
];

export const COMPANY_IMPORT_FIELDS: ReadonlyArray<ImportFieldDescriptor> = [
  {
    field: 'name',
    label: 'Company name',
    required: true,
    synonyms: [
      /^(company|account|organi[sz]ation)?name$/,
      /^company$/,
      /^account$/,
      /^organi[sz]ation$/,
    ],
  },
  {
    field: 'domain',
    label: 'Domain',
    synonyms: [/^(email|company|web)?domain$/],
  },
  {
    field: 'industry',
    label: 'Industry',
    synonyms: [/^industry$/, /^sector$/, /^vertical$/],
  },
  {
    field: 'website',
    label: 'Website',
    synonyms: [/^web(site)?(url)?$/, /^url$/, /^homepage$/, /^site$/],
  },
  {
    field: 'description',
    label: 'Description',
    synonyms: [/^description$/, /^about$/, /^summary$/, /^notes?$/],
  },
  {
    field: 'tags',
    label: 'Tags',
    hint: 'Comma-separated',
    synonyms: [/^tags?$/, /^labels?$/],
  },
];

export const DEAL_IMPORT_FIELDS: ReadonlyArray<ImportFieldDescriptor> = [
  {
    field: 'name',
    label: 'Deal name',
    required: true,
    synonyms: [/^(deal)?(name|title)$/, /^opportunity(name)?$/],
  },
  {
    field: 'stageName',
    label: 'Stage',
    hint: 'Matched to stage names in your pipeline; unmatched rows use the default stage',
    synonyms: [/^(deal|pipeline)?stage(name)?$/, /^status$/],
  },
  {
    field: 'value',
    label: 'Value',
    synonyms: [/^(deal)?(value|amount)$/, /^price$/, /^revenue$/],
  },
  {
    field: 'currency',
    label: 'Currency',
    synonyms: [/^currency(code)?$/],
  },
  {
    field: 'expectedCloseDate',
    label: 'Expected close date',
    synonyms: [/^(expected)?clos(e|ing)date$/, /^expectedclose$/],
  },
  {
    field: 'contactEmail',
    label: 'Contact email',
    hint: 'Links to an existing contact by email',
    synonyms: [/^contact(email)?$/, /^e?mail(address)?$/],
  },
  {
    field: 'companyName',
    label: 'Company',
    hint: 'Links to an existing company by name',
    synonyms: [/^(company|account)(name)?$/, /^organi[sz]ation$/],
  },
  {
    field: 'companyDomain',
    label: 'Company domain',
    hint: 'Links to an existing company by domain',
    synonyms: [/^(company)?domain$/],
  },
  {
    field: 'tags',
    label: 'Tags',
    hint: 'Comma-separated',
    synonyms: [/^tags?$/, /^labels?$/],
  },
  {
    field: 'notes',
    label: 'Notes',
    synonyms: [/^notes?$/, /^comments?$/, /^description$/],
  },
];

export function importFieldsFor(
  entityType: ImportEntityType,
): ReadonlyArray<ImportFieldDescriptor> {
  switch (entityType) {
    case 'contact':
      return CONTACT_IMPORT_FIELDS;
    case 'company':
      return COMPANY_IMPORT_FIELDS;
    case 'deal':
      return DEAL_IMPORT_FIELDS;
  }
}
